import { datadogRum } from '@datadog/browser-rum';
import SearchType from '~/constants/common/SearchType';
import type { SearchSuggestion, Suggestion } from '~/contracts/common';
import { normalizeSearchInputShape } from './utils';

const savedSuggestionsV1Key = 'CVSavedSuggestionsv1';
const savedSuggestionsKey = 'CVSavedSuggestions';

/**
 Function to check if user had recent searches from before the V2 transition.
 If so, we will transfer them over to the new key and delete the old key.
 This ensures that SRP and Homepage recent searches are the same.
 */
function transferSuggestionsFromV1() {
  const recentSearchesFromV1 = JSON.parse(
    localStorage.getItem(savedSuggestionsV1Key) ?? '[]'
  );

  if (recentSearchesFromV1.length > 0) {
    const recentSearchesFromV2 = JSON.parse(
      localStorage.getItem(savedSuggestionsKey) ?? '[]'
    );

    const uniqueRecentSearches = [
      ...recentSearchesFromV1,
      ...recentSearchesFromV2
    ].filter(
      (val, idx, arr) =>
        arr.findIndex(recentSearch => recentSearch.text === val.text) === idx
    );

    localStorage.setItem(
      savedSuggestionsKey,
      JSON.stringify(uniqueRecentSearches)
    );

    localStorage.removeItem(savedSuggestionsV1Key);
  }
}

function removeLegacySearches(
  savedSearches: SearchSuggestion[]
): SearchSuggestion[] {
  const filteredSearches = savedSearches.filter(
    currentItem => !currentItem.hasOwnProperty('legacyFilters')
  );

  if (savedSearches.length !== filteredSearches.length) {
    datadogRum.addAction('Homepage legacyFilters removed'); // TODO: do this some other way
  }

  localStorage.setItem(savedSuggestionsKey, JSON.stringify(filteredSearches));

  return filteredSearches;
}

function getRecentSuggestions(): SearchSuggestion[] {
  transferSuggestionsFromV1();
  const saved = localStorage.getItem(savedSuggestionsKey);

  return saved
    ? removeLegacySearches(
        JSON.parse(saved).map((suggestion: SearchSuggestion) => ({
          ...suggestion,
          isSavedSuggestion: true
        }))
      )
    : [];
}

function isSearchSuggestion(
  v: SearchSuggestion | Suggestion
): v is SearchSuggestion {
  return 'buildKey' in v;
}

export function saveSuggestionToLocalStorage(
  itemToSave: SearchSuggestion | Suggestion
) {
  const recentSearches = getRecentSuggestions();
  const formattedItemToSave: SearchSuggestion = {
    isSavedSuggestion: true,
    text: itemToSave.text,
    highlightedText: itemToSave.highlightedText,
    buildKey: isSearchSuggestion(itemToSave) ? itemToSave.buildKey : 0,
    filters: isSearchSuggestion(itemToSave) ? itemToSave.filters : {}
  };

  const existsInLocalStorage = recentSearches.some(
    (suggestion: SearchSuggestion) =>
      suggestion.text === formattedItemToSave.text
  );

  if (!existsInLocalStorage) {
    recentSearches.unshift(formattedItemToSave);
    localStorage.setItem(savedSuggestionsKey, JSON.stringify(recentSearches));
  }

  return getRecentSuggestions();
}

export function clearRecentSuggestions(): [] {
  localStorage.removeItem(savedSuggestionsKey);

  return [];
}

export function fetchLatestRecentSuggestions() {
  return normalizeSearchInputShape({
    searchInputType: SearchType.RecentSearch,
    data: getRecentSuggestions()
  });
}

export function removeSuggestionFromLocalStorage(
  keywordObject: Suggestion,
  inputValue: string = ''
) {
  const updatedRecentSearches = getRecentSuggestions().filter(
    (suggestion: SearchSuggestion) =>
      suggestion.text.toLowerCase() !== keywordObject.text.toLowerCase()
  );

  localStorage.setItem(
    savedSuggestionsKey,
    JSON.stringify(updatedRecentSearches)
  );

  const recentSuggestions =
    inputValue.length > 0
      ? getRecentSuggestions().filter((recentSuggestion: SearchSuggestion) =>
          recentSuggestion.text.toLowerCase().includes(inputValue.toLowerCase())
        )
      : getRecentSuggestions();

  return normalizeSearchInputShape({
    userInput: inputValue,
    searchInputType: SearchType.RecentSearch,
    data: recentSuggestions
  });
}
